import * as api from "app/api/planning"
import Timeslot from "app/models/timeslot"
import { SagaIterator } from "redux-saga"
import { call, put, takeLatest, take } from "redux-saga/effects"
import { formatDateToWeek } from "utils/formatDateToWeek"
import * as timeslotsChannelActions from "app/redux/planningTimeslotsChannel/planningTimeslotsChannel.actions"
import { notifyIfServerError } from "../utils"
import * as weekActions from "../weeks/weeks.actions"
import * as actions from "./planningTimeslots.actions"
import * as hrUnavailabilityActions from "../hrUnavailabilities/hrUnavailabilities.actions"
import * as requestsActions from "../requests/requests.actions"
import { handleError } from "../errorHandler/errorHandler.saga"

export function* postTimeslots(action: actions.PostTimeslotsAction): SagaIterator {
  try {
    const { data } = action.payload
    const response = yield call(api.postTimeslots, {
      data: {
        ...data,
      },
    })

    const newTimeslot = response && (response.data as Timeslot)
    yield put(actions.postTimeslotsSuccess(newTimeslot as Timeslot))
  } catch (e) {
    yield put(actions.postTimeslotsFail(e))
    yield call(handleError, { error: e })
  }
}

export function* patchTimeslots(action: actions.PatchTimeslotsAction): SagaIterator {
  try {
    const { data, id } = action.payload
    yield call(api.patchTimeslots, id, { data })
    yield put(actions.patchTimeslotsSuccess())
  } catch (e) {
    yield put(actions.patchTimeslotsFail(e))
    yield call(handleError, { error: e })
  }
}

export function* patchTimeslotConflictSuccess(action: actions.PatchTimeslotConflictSuccess): SagaIterator {
  try {
    const { data, timeslotId } = action.payload

    yield put(actions.patchTimeslotConflictSuccess(timeslotId, data))
  } catch (e) {
    yield put(notifyIfServerError({ error: e }))
    yield call(handleError, { error: e })
  }
}

export function* getTimeslots(action: actions.GetTimeslotsAction): SagaIterator {
  try {
    const { params } = action.payload
    yield put(timeslotsChannelActions.disconnectTimeslotsChannel())
    yield put(weekActions.getWeeks(formatDateToWeek(params.from)))
    yield put(timeslotsChannelActions.connectTimeslotsChannel(params))
  } catch (e) {
    yield put(actions.getPlanningTimeslotsFail(e))
    yield put(notifyIfServerError({ error: e }))
  }
}

export function* postTimeslotEmployeeRequest(action: actions.PostTimeslotsEmployeeRequestsAction): SagaIterator {
  try {
    const { data } = action.payload
    yield put(requestsActions.postRequests({ data: [data] }))
    yield take(requestsActions.Types.POST_SUCCESS)
    yield put(actions.postTimeslotsEmployeeRequestsSuccess())
    if (data.date) {
      const week = formatDateToWeek(data.date)
      const response = yield call(api.getHrUnavailabilities, { week })
      yield put(hrUnavailabilityActions.getHrUnavailabilitiesSuccess(response.data))
    }
  } catch (e) {
    yield put(actions.postTimeslotsEmployeeRequestsFail(e))
  }
}

export function* patchTimeslotEmployeeRequest(action: actions.PatchTimeslotsEmployeeRequestsAction): SagaIterator {
  try {
    const { data } = action.payload
    yield put(requestsActions.patchRequests([data]))
    yield take(requestsActions.Types.PATCH_SUCCESS)
    yield put(actions.patchTimeslotsEmployeeRequestsSuccess())
    if (data.date) {
      const week = formatDateToWeek(data.date)
      const response = yield call(api.getHrUnavailabilities, { week })
      yield put(hrUnavailabilityActions.getHrUnavailabilitiesSuccess(response.data))
    }
  } catch (e) {
    yield put(actions.patchTimeslotsEmployeeRequestsFail(e))
  }
}

export function* deleteTimeslots(action: actions.DeleteTimeslotsAction): SagaIterator {
  try {
    const { id } = action.payload
    const response = yield call(api.deleteTimeslotsById, id)
    const deletedTimeslot = response && (response.data as Timeslot)
    yield put(actions.deleteTimeslotsSuccess(deletedTimeslot))
  } catch (e) {
    yield put(actions.deleteTimeslotsFail(e))
    yield call(handleError, { error: e })
  }
}

export default function* planningTimeslotsSaga(): SagaIterator {
  yield takeLatest(actions.Types.POST, postTimeslots)
  yield takeLatest(actions.Types.POST_TIMESLOT_EMPLOYEE_REQUEST, postTimeslotEmployeeRequest)
  yield takeLatest(actions.Types.PATCH_TIMESLOT_EMPLOYEE_REQUEST, patchTimeslotEmployeeRequest)
  yield takeLatest(actions.Types.GET, getTimeslots)
  yield takeLatest(actions.Types.DELETE, deleteTimeslots)
  yield takeLatest(actions.Types.PATCH, patchTimeslots)
}
